import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import adminApi, { setAdminToken } from "@/lib/adminApi";
import BrandMark from "@/components/BrandMark";
import Footer from "@/components/Footer";
import { ChevronLeft } from "lucide-react";

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-stone-500">{label}</span>
    {children}
  </label>
);

const Card = ({ title, kicker, children }) => (
  <div className="bg-white border border-stone-100 rounded-2xl p-6 shadow-[0_2px_8px_rgba(28,25,23,0.04)]">
    {kicker && <div className="text-[10px] font-[Manrope] font-bold tracking-[0.25em] uppercase text-[#6B7A54] mb-2">{kicker}</div>}
    <h2 className="font-[Cormorant_Garamond] italic text-2xl text-stone-900 mb-4">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);

export default function AdminSettings() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [audit, setAudit] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", new_password_confirm: "" });
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResp, setEmailResp] = useState(null);
  const [shop, setShop] = useState({ affiliateMode: false, affiliatePrefix: "" });
  const [shopSaving, setShopSaving] = useState(false);

  const saveShop = async () => {
    setShopSaving(true);
    try {
      const { data } = await adminApi.put("/shop-config", {
        affiliateMode: shop.affiliateMode,
        affiliatePrefix: shop.affiliatePrefix.trim(),
      });
      setShop({ affiliateMode: !!data.affiliateMode, affiliatePrefix: data.affiliatePrefix || "" });
      toast.success("Shop config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setShopSaving(false);
    }
  };

  const load = async () => {
    try {
      const [{ data: me }, { data: al }, { data: ob }, { data: sc }] = await Promise.all([
        adminApi.get("/me"),
        adminApi.get("/audit-log"),
        adminApi.get("/email-outbox"),
        adminApi.get("/shop-config"),
      ]);
      setAdmin(me);
      setAudit(al.logs);
      setOutbox(ob.emails);
      setShop({ affiliateMode: !!sc.affiliateMode, affiliatePrefix: sc.affiliatePrefix || "" });
    } catch {
      navigate("/admin/login", { replace: true });
    }
  };
  useEffect(() => { load(); }, [navigate]);

  const changePwd = async () => {
    setPwdLoading(true);
    try {
      const { data } = await adminApi.post("/change-password", pwd);
      toast.success(data.message);
      setPwd({ current_password: "", new_password: "", new_password_confirm: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setPwdLoading(false); }
  };

  const changeEmail = async () => {
    setEmailLoading(true);
    try {
      const { data } = await adminApi.post("/change-email/request", emailForm);
      setEmailResp(data);
      setEmailForm({ new_email: "", current_password: "" });
      toast.success("Verification link generated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setEmailLoading(false); }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain">
      <div className="max-w-2xl mx-auto px-6 pt-12 pb-12">
        <button onClick={() => navigate("/admin/dashboard")} className="mb-4 flex items-center gap-2 text-sm font-[Manrope] text-stone-600">
          <ChevronLeft size={16} /> back
        </button>
        <BrandMark className="mb-4" to="/admin/dashboard" />
        <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-2">Admin</div>
        <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight tracking-tight text-stone-900 mb-2">
          Settings
        </h1>
        <p className="text-sm font-[Manrope] text-stone-600 mb-8">{admin?.email}</p>

        {admin?.last_login_at && (
          <div className="mb-6 rounded-2xl border border-stone-200 bg-white p-4 text-sm font-[Manrope] text-stone-700">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-stone-500 mb-1">Last login</div>
            <div>{new Date(admin.last_login_at).toLocaleString()} · from IP <span className="font-mono">{admin.last_login_ip || "?"}</span></div>
          </div>
        )}

        <div className="flex flex-col gap-6">
          <Card title="Change password" kicker="Security">
            <p className="text-sm text-stone-600">Minimum 10 characters, must include letters and numbers. Other active sessions will be signed out.</p>
            <Field label="Current password"><input data-testid="admin-current-password" type="password" className="input" value={pwd.current_password} onChange={(e)=>setPwd({...pwd, current_password:e.target.value})}/></Field>
            <Field label="New password"><input data-testid="admin-new-password" type="password" className="input" value={pwd.new_password} onChange={(e)=>setPwd({...pwd, new_password:e.target.value})}/></Field>
            <Field label="Confirm new password"><input data-testid="admin-confirm-password" type="password" className="input" value={pwd.new_password_confirm} onChange={(e)=>setPwd({...pwd, new_password_confirm:e.target.value})}/></Field>
            <button data-testid="admin-change-password-btn" onClick={changePwd} disabled={pwdLoading} className="btn-primary">
              {pwdLoading ? "Updating…" : "Update password"}
            </button>
          </Card>

          <Card title="Change email" kicker="Security">
            <p className="text-sm text-stone-600">We&rsquo;ll email a verification link to your new address; the change only applies after you click it. Your old address is notified.</p>
            <Field label="New email"><input data-testid="admin-new-email" type="email" className="input" value={emailForm.new_email} onChange={(e)=>setEmailForm({...emailForm, new_email:e.target.value})}/></Field>
            <Field label="Current password"><input data-testid="admin-email-current-password" type="password" className="input" value={emailForm.current_password} onChange={(e)=>setEmailForm({...emailForm, current_password:e.target.value})}/></Field>
            <button data-testid="admin-change-email-btn" onClick={changeEmail} disabled={emailLoading} className="btn-primary">
              {emailLoading ? "Sending…" : "Send verification link"}
            </button>
            {emailResp?.mock_link && (
              <div className="rounded-xl border border-[#6B7A54]/30 bg-[#F2EFEA] p-3 text-xs font-[Manrope] text-stone-700">
                <div className="font-bold mb-1">Mock email link (email delivery not configured):</div>
                <Link to={emailResp.mock_link} className="text-[#6B7A54] underline break-all">{emailResp.mock_link}</Link>
              </div>
            )}
          </Card>

          <Card title="Shop links · affiliate" kicker="Style Gaps & Shop">
            <p className="text-xs text-stone-500 mb-2">
              Toggle affiliate wrapping. When ON, every &ldquo;Get it on Myntra&rdquo; button in the app
              is routed through your prefix (EarnKaro, Cuelinks, etc.). Toggle OFF for vanilla Myntra links.
            </p>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                data-testid="admin-shop-affiliate-toggle"
                type="checkbox"
                checked={shop.affiliateMode}
                onChange={(e) => setShop({ ...shop, affiliateMode: e.target.checked })}
                className="h-5 w-5 accent-[#6B7A54]"
              />
              <span className="text-sm font-[Manrope] text-stone-800">
                Affiliate mode {shop.affiliateMode ? "ON" : "OFF"}
              </span>
            </label>
            <Field label="Affiliate prefix (URL that wraps the Myntra link)">
              <input
                data-testid="admin-shop-affiliate-prefix"
                type="text"
                className="input font-mono text-xs"
                placeholder="https://ekaro.in/enkr20250101/?url="
                value={shop.affiliatePrefix}
                onChange={(e) => setShop({ ...shop, affiliatePrefix: e.target.value })}
              />
            </Field>
            <div className="text-[11px] font-[Manrope] text-stone-500 leading-relaxed">
              Example: <span className="font-mono">https://ekaro.in/enkr20250101/?url=</span> &nbsp;→&nbsp; final URL becomes
              &nbsp;<span className="font-mono">{shop.affiliatePrefix || "PREFIX"}{encodeURIComponent("https://www.myntra.com/kurtas?rawQuery=pastel+cotton+kurta")}</span>
            </div>
            <button
              data-testid="admin-shop-save"
              onClick={saveShop}
              disabled={shopSaving}
              className="btn-primary"
            >
              {shopSaving ? "Saving…" : "Save shop config"}
            </button>
          </Card>

          <Card title="Audit log" kicker="Security">
            <div className="text-xs font-[Manrope] text-stone-500 mb-2">Recent admin actions (login, credential changes).</div>
            <div className="max-h-64 overflow-y-auto flex flex-col gap-2">
              {audit.length === 0 && <div className="text-sm text-stone-500">No events yet.</div>}
              {audit.map((e) => (
                <div key={`${e.at}-${e.action}`} className="flex items-start justify-between gap-4 py-2 border-b border-stone-100 last:border-0">
                  <div className="text-sm">
                    <div className="font-medium text-stone-800">{e.action.replaceAll("_", " ")}</div>
                    <div className="text-xs text-stone-500">{e.location} · {e.ip}</div>
                  </div>
                  <div className="text-xs font-mono text-stone-500 shrink-0">{new Date(e.at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Email outbox (mock)" kicker="Local delivery">
            <p className="text-xs text-stone-500">Real email delivery is not wired yet. Verification and reset links appear here.</p>
            <div className="max-h-64 overflow-y-auto flex flex-col gap-2">
              {outbox.length === 0 && <div className="text-sm text-stone-500">No emails sent yet.</div>}
              {outbox.map((e) => (
                <div key={`${e.sent_at}-${e.to}`} className="py-2 border-b border-stone-100 last:border-0 text-sm">
                  <div className="text-xs text-stone-500">to: {e.to} · {new Date(e.sent_at).toLocaleString()}</div>
                  <div className="font-medium text-stone-800">{e.subject}</div>
                  <div className="text-stone-600 mt-1">{e.body}</div>
                  {e.link && <Link to={e.link} className="text-[#6B7A54] underline text-xs break-all">{e.link}</Link>}
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Footer />
      </div>
    </div>
  );
}
